/**
 * Atlas Advisor suggested questions API client and react-query hook.
 * Only used when shouldShowAtlasAdvisorNav() is true (Atlas app).
 *
 * V2: looks up by (entityType, tab, subTab) against the V2 DynamoDB table.
 */

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { shouldShowAtlasAdvisorNav } from 'src/utils/app';

const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches suggested questions using entityType, tab and subTab.
 * GET /api/Atlas/GetSuggestedQuestionsV2?entityType=...&tab=...&subTab=...
 */
export async function fetchAdvisorSuggestedQuestions(
  entityType: string,
  tab: string,
  subTab: string
): Promise<string[]> {
  const { data } = await axios.get<string[]>('/api/Atlas/GetSuggestedQuestionsV2', {
    params: { entityType, tab, subTab }
  });
  return Array.isArray(data) ? data : [];
}

/**
 * React-query hook for Atlas Advisor suggested questions (V2).
 * Pass entityType (from URL path / useMainEntityType), tab and subTab from URL;
 * enabled when all three are set and shouldShowAtlasAdvisorNav() is true.
 */
export function useAdvisorSuggestedQuestions(
  entityType: string | undefined,
  tab: string | undefined,
  subTab: string | undefined
) {
  const enabled = Boolean(entityType && tab && subTab) && shouldShowAtlasAdvisorNav();

  return useQuery({
    queryKey: ['advisorSuggestedQuestions', entityType, tab, subTab],
    queryFn: () => fetchAdvisorSuggestedQuestions(entityType!, tab!, subTab!),
    enabled,
    staleTime: STALE_TIME_MS
  });
}
